import type { Activity, CallDisposition, Contact } from '../types'

export type VoiceSessionState = 'ringing' | 'connected' | 'ended'
export type VoiceSession = {
  id: string
  direction: 'INBOUND' | 'OUTBOUND'
  state: VoiceSessionState
  phone: string
  contactId: string | null
  startedAt: string
  connectedAt: string | null
  endedAt: string | null
  durationSeconds: number
  muted: boolean
}

export type VoiceHistoryCall = {
  id: string
  attemptId?: string
  contactId: string
  organizationId?: string
  actorUserId: string
  direction: 'INBOUND' | 'OUTBOUND'
  status: string
  durationSeconds: number
  startedAt: string
  answeredAt: string | null
  completedAt: string | null
  updatedAt: string
}

export type CallWorkspaceDirectory = {
  contacts: Contact[]
  activities: Activity[]
  dispositions: CallDisposition[]
}

const E164 = /^\+[1-9]\d{7,14}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const formatCallDuration = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const remaining = safe % 60
  return hours ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}` : `${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`
}

export const resolveCaller = (phone: string, contactId: string | null, contacts: Contact[]) => {
  const normalized = phone.trim()
  if (!E164.test(normalized)) return null
  const byId = contactId ? contacts.find(contact => contact.id === contactId && (contact.phoneE164 || contact.phone.trim()) === normalized) : null
  if (byId) return byId
  const matches = contacts.filter(contact => (contact.phoneE164 || contact.phone.trim()) === normalized)
  return matches.length === 1 ? matches[0] : null
}

export const decodeIncomingContactId = (context: string | undefined, from: string) => {
  if (!context || !E164.test(from.trim())) return null
  const payload = context.split('.')[0]
  if (!payload) return null
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as { contactId?: unknown; from?: unknown; direction?: unknown; exp?: unknown }
    if (decoded.direction !== 'INBOUND' || decoded.from !== from.trim() || !UUID.test(String(decoded.contactId || '')) || Number(decoded.exp) <= Math.floor(Date.now() / 1000)) return null
    return String(decoded.contactId)
  } catch {
    return null
  }
}

export const latestContactInteraction = (contactId: string, calls: VoiceHistoryCall[], activities: Activity[], dispositions: CallDisposition[]) => {
  const completedCalls = calls
    .filter(call => call.contactId === contactId && call.completedAt && call.status === 'completed')
    .map(call => ({ occurredAt: call.completedAt as string, actorUserId: call.actorUserId, outcome: call.direction === 'INBOUND' ? 'Completed inbound call' : 'Completed outbound call', channel: 'Voice' }))
  const callActivities = activities
    .filter(activity => activity.contactId === contactId && activity.type === 'call')
    .map(activity => ({ occurredAt: activity.occurredAt, actorUserId: activity.actorUserId || '', outcome: activity.title || activity.detail || 'Call activity', channel: 'Call' }))
  const outcomes = dispositions
    .filter(disposition => disposition.contactId === contactId)
    .map(disposition => ({ occurredAt: disposition.occurredAt, actorUserId: disposition.actorUserId, outcome: disposition.disposition, channel: 'Call outcome' }))
  return [...completedCalls, ...callActivities, ...outcomes].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt))[0] || null
}
